import React from "react";
import { fetchForms, calculer } from "../../light/api";
import { LightForm, LightCalculationResult, LightArticleInput, LightOptions } from "../../light/types";
import ModalSelectionFormeLight from "./components/ModalSelectionFormeLight";
import { LightDimensionForm } from "../../components/light/LightDimensionForm";
import { LightOptionsForm } from "../../components/light/LightOptionsForm";
import { PreviewPane } from "../../components/light/PreviewPane";

const ICON_MAP: Record<string, string> = {
  rectangulaire_bandeau: "/images/formes/coussins/rectanglulaire_bandeau.png",
  rectangulaire_joues: "/images/formes/coussins/rectanglulaire_joues.png",
  rectangulaire_demi_bandeau: "/images/formes/coussins/rectanglulaire_demi_bandeau.png",
  rectangle_demi_bandeau_biseau: "/images/formes/coussins/rectangle_demi_bandeau_biseau.png",
  rectangle_joues_biseau: "/images/formes/coussins/Rectangle_joues_biseau.png",
  rectangle_boite: "/images/formes/coussins/rectangle_boite.png",
  rond_avec_bandeau: "/images/formes/coussins/rond_avec_bandeau.png",
  rond_bandeau: "/images/formes/coussins/rond_avec_bandeau.png",
  cylindre_joues: "/images/formes/coussins/cylindre_joues.png",
  rectangle_simple: "/images/formes/coussins/Rectangle_simple.png",
};

const SCHEMA_MAP: Record<string, string> = {
  rectangulaire_bandeau: "/images/formes/coussins/rectanglulaire_bandeau_fleches.png",
  rectangulaire_joues: "/images/formes/coussins/rectanglulaire_joues_fleche.png",
  rectangulaire_demi_bandeau: "/images/formes/coussins/rectanglulaire_demi_bandeau_fleches.png",
  rectangle_demi_bandeau_biseau: "/images/formes/coussins/rectangle_demi_bandeau_biseau_fleches.png",
  rectangle_joues_biseau: "/images/formes/coussins/Rectangle_joues_biseau_fleches.png",
  rectangle_boite: "/images/formes/coussins/rectangle_boite_fleches.png",
  rond_avec_bandeau: "/images/formes/coussins/rond_avec_bandeau_fleches.png",
  rond_bandeau: "/images/formes/coussins/rond_avec_bandeau_fleches.png",
  cylindre_joues: "/images/formes/coussins/cylindre_joues_fleches.png",
  rectangle_simple: "/images/formes/coussins/Rectangle_simple_fleches.png",
};

const SCHEMA_MAP_SANS_FOND: Record<string, string> = {
  rectangulaire_bandeau: "/images/formes/coussins/rectanglulaire_bandeau_fleches_sans_fond.png",
  rectangulaire_joues: "/images/formes/coussins/rectanglulaire_joues_fleche_sans_fond.png",
  rectangulaire_demi_bandeau: "/images/formes/coussins/rectanglulaire_demi_bandeau_fleches_sans_fond.png",
  rectangle_demi_bandeau_biseau: "/images/formes/coussins/rectangle_demi_bandeau_biseau_fleches_sans_fond.png",
  rectangle_joues_biseau: "/images/formes/coussins/Rectangle_joues_biseau_fleches_sans_fond.png",
  rectangle_boite: "/images/formes/coussins/rectangle_boite_fleches_sans_fond.png",
  rond_avec_bandeau: "/images/formes/coussins/rond_avec_bandeau_fleches_sans_fond.png",
};

const getSchemaForForm = (formId?: string, fondEnabled?: boolean) => {
  if (!formId) return undefined;
  if (fondEnabled === false && SCHEMA_MAP_SANS_FOND[formId]) {
    return SCHEMA_MAP_SANS_FOND[formId];
  }
  return SCHEMA_MAP[formId];
};

const labelForme = (form?: LightForm) => form?.nom || form?.id || "";

const STORAGE_KEY = "light:draft";
const DECIMAL_INPUT_PATTERN = "[0-9]*([.,][0-9]{0,2})?";
const DEFAULT_SUPPLEMENT_BORD = 3;

const normalizeLightOptions = (options: LightOptions = {}): LightOptions => {
  const fond = options.fond ?? false;
  const grille = false; // Grille non exposÃ©e en Light mais doit rester false dans les payloads
  const talonAgrafageActif = fond ? false : options.talonAgrafageActif ?? true;
  const talonAgrafage = fond ? options.talonAgrafage : options.talonAgrafage ?? DEFAULT_SUPPLEMENT_BORD;

  return {
    ...options,
    fond,
    grille,
    fermeture: options.fermeture ?? false,
    talonAgrafageActif,
    talonAgrafage,
  };
};

type LightDraft = {
  laize?: number;
  selectedForm?: string;
  dimensions?: Record<string, number | undefined>;
  options?: LightOptions;
  quantite?: number;
  articles?: LightArticleInput[];
};

const loadDraft = (): LightDraft | null => {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    return raw ? (JSON.parse(raw) as LightDraft) : null;
  } catch {
    return null;
  }
};

const saveDraft = (draft: LightDraft) => {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(draft));
  } catch {
    // ignore storage errors (quota/private mode)
  }
};

const clearDraft = () => {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.removeItem(STORAGE_KEY);
  } catch {
    // ignore
  }
};

const ArticleConfigurationLight: React.FC = () => {
  const [forms, setForms] = React.useState<LightForm[]>([]);
  const [selectedForm, setSelectedForm] = React.useState<string>();
  const [dimensions, setDimensions] = React.useState<Record<string, number | undefined>>({});
  const [options, setOptions] = React.useState<LightOptions>(normalizeLightOptions({}));
  const [quantite, setQuantite] = React.useState<number>(1);
  const [quantiteInput, setQuantiteInput] = React.useState<string>("1");
  const [laize, setLaize] = React.useState<number>(150);
  const [laizeInput, setLaizeInput] = React.useState<string>("150");
  const [laizeLocked, setLaizeLocked] = React.useState<boolean>(false);
  const [articles, setArticles] = React.useState<LightArticleInput[]>([]);
  const [isAdding, setIsAdding] = React.useState<boolean>(true);
  const [result, setResult] = React.useState<LightCalculationResult>();
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<string>();
  const [showFormeModal, setShowFormeModal] = React.useState(false);
  const [draftRestored, setDraftRestored] = React.useState(false);

  React.useEffect(() => {
    fetchForms()
      .then((res) => {
        setForms(res.forms);
      })
      .catch((e) => setError(e.message));
  }, []);

  React.useEffect(() => {
    const draft = loadDraft();
    if (draft) {
      if (draft.laize) {
        setLaize(draft.laize);
        setLaizeInput(String(draft.laize));
      }
      if (draft.selectedForm) setSelectedForm(draft.selectedForm);
      setDimensions(draft.dimensions || {});
      setOptions(normalizeLightOptions(draft.options || {}));
      const q = draft.quantite || 1;
      setQuantite(q);
      setQuantiteInput(String(q));
      if (draft.articles && draft.articles.length > 0) {
        const normalizedArticles = draft.articles.map((a) => ({
          ...a,
          options: normalizeLightOptions(a.options),
        }));
        setArticles(normalizedArticles);
        setLaizeLocked(true);
        setIsAdding(false);
      }
    }
    setDraftRestored(true);
  }, []);

  const currentForm = forms.find((f) => f.id === selectedForm);

  // Init options par défaut selon la forme
  React.useEffect(() => {
    if (!currentForm) {
      setOptions(normalizeLightOptions({}));
      return;
    }
    if (currentForm.id === "rectangle_simple") {
      setOptions((prev) => ({
        ...prev,
        fond: undefined,
        fermeture: undefined,
        grille: false,
        talonAgrafageActif: undefined,
        talonAgrafage: undefined,
      }));
    } else {
      setOptions((prev) => normalizeLightOptions(prev));
    }
  }, [currentForm]);

  React.useEffect(() => {
    if (!draftRestored) return;
    const hasData =
      articles.length > 0 ||
      !!selectedForm ||
      Object.keys(dimensions).length > 0 ||
      Object.keys(options).length > 0 ||
      quantite !== 1 ||
      laize !== 150;
    if (!hasData) {
      clearDraft();
      return;
    }
    saveDraft({
      laize,
      selectedForm,
      dimensions,
      options,
      quantite,
      articles,
    });
  }, [draftRestored, laize, selectedForm, dimensions, options, quantite, articles]);

  const onCalculate = async (articlesToCalc: LightArticleInput[], laizeOverride?: number) => {
    if (!articlesToCalc.length) {
      setError("Ajoutez au moins un article avant de calculer.");
      return;
    }
    setError(undefined);
    setLoading(true);
    try {
      const res = await calculer({
        laize: laizeOverride ?? laize,
        articles: articlesToCalc,
      });
      setResult(res);
    } catch (e: any) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  React.useEffect(() => {
    if (!draftRestored) return;
    if (articles.length) {
      onCalculate(articles, laize);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draftRestored]);

  const addArticle = () => {
    if (!currentForm) {
      setError("Sélectionnez d'abord une forme.");
      return;
    }
    const required = currentForm.dimensions || [];
    const hasBiseau = required.includes("l1") || required.includes("l2");
    const effectiveRequired = hasBiseau ? required.filter((d) => d !== "largeur") : required;
    const missing = effectiveRequired.filter((k) => !dimensions[k]);
    if (missing.length) {
      setError(`Champs manquants : ${missing.join(", ")}`);
      return;
    }
    const articleOptions = normalizeLightOptions(options);
    const article: LightArticleInput = {
      forme: currentForm.id,
      dimensions: Object.fromEntries(Object.entries(dimensions).filter(([, v]) => v !== undefined)) as Record<string, number>,
      options: articleOptions,
      quantite: quantite,
    };
    const nextArticles = [...articles, article];
    setArticles(nextArticles);
    setLaizeLocked(true);
    setIsAdding(false);
    // reset saisie pour la prochaine création
    setSelectedForm(undefined);
    setDimensions({});
    setOptions(normalizeLightOptions({}));
    setQuantite(1);
    setQuantiteInput("1");
    onCalculate(nextArticles);
  };

  const removeArticle = (idx: number) => {
    const next = articles.filter((_, i) => i !== idx);
    setArticles(next);
    if (!next.length) {
      setResult(undefined);
      setLaizeLocked(false);
      setIsAdding(true);
    } else {
      onCalculate(next);
    }
  };

  const handleLaizeChange = (val: string) => {
    setLaizeInput(val);
  };

  const handleLaizeBlur = () => {
    const normalized = laizeInput.replace(",", ".");
    const num = Number(normalized);
    if (!Number.isFinite(num) || num <= 0) {
      setLaizeInput(String(laize));
      return;
    }
    const rounded = Math.round(num * 100) / 100;
    if (rounded === laize) {
      setLaizeInput(String(rounded));
      return;
    }
    if (articles.length > 0 && laizeLocked) {
      const ok = window.confirm(
        "Cette modification entraine le recalcul pour tous les coussins/bâches du projet. Une seule laize possible pour l'ensemble du projet."
      );
      if (!ok) {
        setLaizeInput(String(laize));
        return;
      }
    }
    setResult(undefined);
    setLaize(rounded);
    setLaizeInput(String(rounded));
    if (articles.length > 0) {
      onCalculate(articles.map((a) => ({ ...a, quantite: a.quantite || 1 })), rounded);
    }
  };

  const handleQuantiteChange = (val: string) => {
    setQuantiteInput(val);
  };

  const handleQuantiteBlur = () => {
    const trimmed = quantiteInput.trim();
    if (!trimmed) {
      setQuantite(1);
      setQuantiteInput("1");
      return;
    }
    const normalized = trimmed.replace(",", ".");
    const num = Number.parseInt(normalized, 10);
    if (!Number.isFinite(num) || num < 1) {
      setQuantite(1);
      setQuantiteInput("1");
      return;
    }
    setQuantite(num);
    setQuantiteInput(String(num));
  };

  const resetDraft = () => {
    clearDraft();
    setArticles([]);
    setSelectedForm(undefined);
    setDimensions({});
    setOptions(normalizeLightOptions({}));
    setQuantite(1);
    setQuantiteInput("1");
    setLaize(150);
    setLaizeInput("150");
    setLaizeLocked(false);
    setIsAdding(true);
    setResult(undefined);
    setError(undefined);
  };

  const downloadPlan = () => {
    if (!result?.calepinagePlan) return;
    const a = document.createElement("a");
    a.href = `data:image/png;base64,${result.calepinagePlan}`;
    a.download = `plan-complet-${result.calculationId || "light"}.png`;
    a.click();
  };

  const currentSchema = currentForm ? getSchemaForForm(currentForm.id, options.fond) : undefined;

  return (
    <div className="max-w-6xl mx-auto p-6 space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900">Configurateur Light</h1>
        <div className="text-sm text-gray-500">Flow simplifié basé sur le configurateur Pro</div>
      </div>

      {error && <div className="p-3 rounded border border-red-200 bg-red-50 text-red-700 text-sm">{error}</div>}

      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 space-y-3">
        <div className="flex items-start justify-between gap-4">
          <div className="flex-1 space-y-2">
            <label className="text-sm font-semibold text-gray-800">Laize du tissu (cm)</label>
            <input
              type="text"
              inputMode="decimal"
              pattern={DECIMAL_INPUT_PATTERN}
              className="w-40 rounded border border-gray-300 px-3 py-2 focus:border-blue-500 focus:outline-none"
              value={laizeInput}
              onChange={(e) => handleLaizeChange(e.target.value)}
              onBlur={handleLaizeBlur}
            />
            <p className="text-xs text-gray-500">
              Cette largeur s'applique à l'ensemble du projet. Brouillon stocké dans votre navigateur, aucune sauvegarde côté serveur.
            </p>
          </div>
          <button
            type="button"
            onClick={resetDraft}
            className="px-4 py-2 rounded border border-gray-300 text-gray-700 hover:bg-gray-50"
          >
            Réinitialiser
          </button>
        </div>
      </div>

      {!currentForm && !articles.length && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 space-y-3">
          <h2 className="text-xl font-semibold text-gray-900">Commencez par choisir une forme</h2>
          <p className="text-sm text-gray-600">
            Choisissez la forme qui se rapproche le plus de votre projet (coussin, housse, bâche...). Vous pourrez ensuite saisir vos
            dimensions et vos options.
          </p>
          <button
            type="button"
            onClick={() => setShowFormeModal(true)}
            className="inline-flex items-center justify-center px-5 py-3 rounded-lg bg-blue-600 text-white font-semibold hover:bg-blue-700"
          >
            Choisir une forme
          </button>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        <div className="lg:col-span-2 space-y-4">
          {isAdding ? (
            <>
              <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5 space-y-4">
                <div className="flex items-center justify-between">
                  <span className="text-lg font-semibold text-gray-900">Forme</span>
                  <button
                    type="button"
                    onClick={() => setShowFormeModal(true)}
                    className="px-4 py-2 rounded bg-blue-600 text-white hover:bg-blue-700"
                  >
                    {currentForm ? "Changer de forme" : "Choisir une forme"}
                  </button>
                </div>
                {currentForm ? (
                  <div className="flex items-center gap-4 border rounded-lg p-3 bg-gray-50">
                    {ICON_MAP[currentForm.id] ? (
                      <img src={ICON_MAP[currentForm.id]} alt={labelForme(currentForm)} className="w-24 h-16 object-contain rounded border" />
                    ) : (
                      <div className="w-24 h-16 flex items-center justify-center rounded border border-gray-200 bg-white">{currentForm.id}</div>
                    )}
                    <div className="flex flex-col">
                      <span className="font-semibold text-gray-900">{labelForme(currentForm)}</span>
                    </div>
                  </div>
                ) : (
                  <div className="text-sm text-gray-500">Aucune forme sélectionnée.</div>
                )}
              </div>

              {currentForm && (
                <>
                  <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5 space-y-4">
                    <div className="flex items-center gap-2">
                      <span className="text-lg font-semibold text-gray-900">Dimensions</span>
                      <span className="text-xs text-gray-500">Reprend les codes couleur Pro (L/l/H/Talon...)</span>
                    </div>
                    <LightDimensionForm form={currentForm} values={dimensions} onChange={setDimensions} />
                  </div>

                  {currentForm.id !== "rectangle_simple" && (
                    <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5 space-y-4">
                      <div className="flex items-center gap-2">
                        <span className="text-lg font-semibold text-gray-900">Options</span>
                        <span className="text-xs text-gray-500">(Light) Fond, Fermeture</span>
                      </div>
                      <LightOptionsForm options={options} onChange={(next) => setOptions(normalizeLightOptions(next))} />
                    </div>
                  )}

                  <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5 space-y-3">
                    <label className="text-sm font-semibold text-gray-800">Quantité</label>
                    <input
                      type="text"
                      inputMode="numeric"
                      pattern="[0-9]*"
                      className="w-28 rounded border border-gray-300 px-3 py-2 focus:border-blue-500 focus:outline-none"
                      value={quantiteInput}
                      onChange={(e) => handleQuantiteChange(e.target.value)}
                      onBlur={handleQuantiteBlur}
                    />
                  </div>

                  <div className="flex gap-3">
                    <button
                      type="button"
                      onClick={addArticle}
                      className="px-4 py-2 rounded bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50"
                      disabled={loading}
                    >
                      Ajouter cet article au projet
                    </button>
                  </div>
                </>
              )}
            </>
          ) : (
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5 space-y-3">
              <div className="text-sm text-gray-700">Créer un nouveau coussin / une nouvelle bâche pour le projet.</div>
              <button
                type="button"
                onClick={() => {
                  setIsAdding(true);
                  setSelectedForm(undefined);
                  setDimensions({});
                  setOptions(normalizeLightOptions({}));
                  setQuantite(1);
                  setQuantiteInput("1");
                  setShowFormeModal(true);
                }}
                className="px-4 py-2 rounded bg-blue-600 text-white hover:bg-blue-700"
              >
                Nouvelle forme (coussin / bâche)
              </button>
            </div>
          )}

          {articles.length > 0 && (
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5 space-y-3">
              <div className="flex items-center justify-between">
                <div>
                  <span className="text-lg font-semibold text-gray-900">Votre projet ({articles.length} article(s))</span>
                </div>
                <button
                  type="button"
                  onClick={() => onCalculate(articles)}
                  className="px-4 py-2 rounded bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-50"
                  disabled={loading}
                >
                  Recalculer le plan global
                </button>
              </div>
              <ul className="space-y-2">
                {articles.map((a, idx) => {
                  const form = forms.find((f) => f.id === a.forme);
                  const dims = a.dimensions || {};
                  const resumeDims = ["longueur", "largeur", "hauteur", "diametre", "l1", "l2", "talon", "ourlet"]
                    .filter((k) => dims[k] !== undefined)
                    .map((k) => `${k} ${dims[k]} cm`)
                    .join(" · ");
                  return (
                    <li key={`${a.forme}-${idx}`} className="flex items-center justify-between rounded border border-gray-200 px-3 py-2 bg-gray-50">
                      <div className="flex flex-col">
                        <span className="font-semibold text-gray-900">{form ? labelForme(form) : a.forme}</span>
                        <span className="text-xs text-gray-600">{resumeDims}</span>
                        <span className="text-xs text-gray-500">Quantité : {a.quantite || 1}</span>
                      </div>
                      <button
                        type="button"
                        onClick={() => removeArticle(idx)}
                        className="text-sm text-red-600 hover:text-red-800"
                      >
                        Supprimer
                      </button>
                    </li>
                  );
                })}
              </ul>
            </div>
          )}
        </div>

        <div className="space-y-4">
          <PreviewPane metrage={result?.metrage} previewBase64={result?.calepinagePreview} isLoading={loading} />
          <div className="flex flex-col gap-2">
            <button
              type="button"
              onClick={resetDraft}
              className="px-4 py-2 rounded border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-50"
            >
              Réinitialiser le brouillon
            </button>
            {result?.calepinagePlan && (
              <button
                type="button"
                onClick={downloadPlan}
                className="px-4 py-2 rounded bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50"
              >
                Télécharger le plan complet
              </button>
            )}
          </div>
          {result && (
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 space-y-2">
              <div className="text-sm font-semibold text-gray-900">Récapitulatif</div>
              <div className="text-sm text-gray-800">Longueur de tissu : {result.longueurMetres?.toFixed(2)} m</div>
              {result.surfaceMetresCarres !== undefined && (
                <div className="text-sm text-gray-800">Surface utilisée : {result.surfaceMetresCarres?.toFixed(3)} m²</div>
              )}
              {result.calculationId && (
                <div className="text-xs text-gray-500">Calcul {result.calculationId} - aucune persistance serveur.</div>
              )}
            </div>
          )}
          {currentForm && (
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4">
              <h3 className="text-sm font-semibold text-gray-900 mb-2">Correspondance visuelle</h3>
              {currentSchema ? (
                <img src={currentSchema} alt={labelForme(currentForm)} className="w-full h-40 object-contain rounded border" />
              ) : (
                <div className="text-sm text-gray-500">Aucune image disponible.</div>
              )}
              <p className="text-xs text-gray-500 mt-2">
                Repérez vos dimensions en couleurs : Longueur (rouge), Largeur (vert), Hauteur (bleu), Talon (orange). Pour obtenir une
                version téléchargeable/imprimable du plan complet, utilisez le bouton dédié ci-dessus.
              </p>
            </div>
          )}
        </div>
      </div>

      <ModalSelectionFormeLight isOpen={showFormeModal} forms={forms} onSelect={(id: string) => setSelectedForm(id)} onClose={() => setShowFormeModal(false)} />
    </div>
  );
};

export default ArticleConfigurationLight;
